<script setup lang="ts">
import type { PageKey } from '~/types/rbac'
import { provideUseId } from '@headlessui/vue'
import { toast, VSonner } from 'vuetify-sonner'
// Required for snackbar background and text color
import 'vuetify-sonner/style.css'

provideUseId(() => useId().replace(/[-:]/, '_'))

const theme = useTheme()
const route = useRoute()

// Pages that need the full viewport width (e.g. the Mass Distribution
// pipeline canvas) opt in via `definePageMeta({ wide: true })`; every other
// page keeps Vuetify's normal centered, max-width container.
const isWide = computed(() => (route.meta as any).wide === true)

const user = useSupabaseUser()

// Ensure permissions are loaded (used by route gating / pages); the sidebar
// itself shows all pages regardless.
const { load } = usePermissions()
void load()

function toggleTheme() {
  theme.global.name.value = theme.global.current.value.dark ? 'light' : 'dark'
}

const drawer = ref(true)
const rail = ref(true)

interface NavLink {
  icon: string
  title: string
  to: string
  /** The page-permission key that gates this entry, when applicable. */
  page?: PageKey
  /** When true, the entry is only shown to admins. */
  adminOnly?: boolean
}

const allLinks: NavLink[] = [
  { icon: 'material-symbols:dashboard', title: 'Dashboard', to: '/dashboard', page: 'dashboard' },
  { icon: 'material-symbols:archive', title: 'Import', to: '/import/tiktok', page: 'import' },
  { icon: 'material-symbols:schedule', title: 'Schedules', to: '/schedules', page: 'schedules' },
  { icon: 'material-symbols:campaign', title: 'Mass Distribution', to: '/massdistribution', page: 'massdistribution' },
  { icon: 'material-symbols:comment', title: 'Comments', to: '/comments', page: 'comments' },
  { icon: 'material-symbols:notifications', title: 'Notifications', to: '/notifications', page: 'notifications' },
  { icon: 'material-symbols:admin-panel-settings', title: 'Admin', to: '/admin', adminOnly: true },
]

// Show every page in the sidebar by default. Access is enforced per-route by
// the RBAC middleware (and server-side), which sends disallowed users to the
// access-denied view — so the nav itself stays simple and complete.
const links = computed(() => allLinks)

watch(theme.global.name, themeTransition)

function themeTransition() {
  // Previously this started with a 10-million-iteration busy loop used as a
  // "slow device" heuristic; it froze the main thread for hundreds of
  // milliseconds on every theme toggle, so it was removed.
  const el = document.querySelector('#__nuxt')
  if (!el) return
  const children = el.querySelectorAll('*')

  children.forEach((el) => {
    if (hasScrollbar(el)) {
      el.dataset.scrollX = String(el.scrollLeft)
      el.dataset.scrollY = String(el.scrollTop)
    }
  })

  const copy = el.cloneNode(true)
  copy.classList.add('app-copy')
  const rect = el.getBoundingClientRect()
  copy.style.top = `${rect.top}px`
  copy.style.left = `${rect.left}px`
  copy.style.width = `${rect.width}px`
  copy.style.height = `${rect.height}px`

  const targetEl = document.activeElement
  const targetRect = targetEl.getBoundingClientRect()
  const left = targetRect.left + targetRect.width / 2 + window.scrollX
  const top = targetRect.top + targetRect.height / 2 + window.scrollY
  el.style.setProperty('--clip-pos', `${left}px ${top}px`)
  el.style.removeProperty('--clip-size')

  nextTick(() => {
    el.classList.add('app-transition')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.setProperty('--clip-size', `${Math.hypot(window.innerWidth, window.innerHeight)}px`)
      })
    })
  })

  document.body.append(copy)

  copy.querySelectorAll('[data-scroll-x], [data-scroll-y]').forEach((el) => {
    el.scrollLeft = +el.dataset.scrollX
    el.scrollTop = +el.dataset.scrollY
  })

  function onTransitionend(e) {
    if (e.target === e.currentTarget) {
      copy.remove()
      el.removeEventListener('transitionend', onTransitionend)
      el.removeEventListener('transitioncancel', onTransitionend)
      el.classList.remove('app-transition')
      el.style.removeProperty('--clip-size')
      el.style.removeProperty('--clip-pos')
    }
  }
  el.addEventListener('transitionend', onTransitionend)
  el.addEventListener('transitioncancel', onTransitionend)
}

function hasScrollbar(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false

  const style = window.getComputedStyle(el)
  return style.overflowY === 'scroll' || (style.overflowY === 'auto' && el.scrollHeight > el.clientHeight)
}
</script>

<template>
  <div class="border rounded">
    <v-app>
      <VSonner />
      <NuxtLoadingIndicator color="#FFFFFF" :throttle="0" />

      <v-app-bar>
        <!-- <v-app-bar-nav-icon variant="text" @click.stop="rail = !rail" /> -->

        <v-toolbar-title>cloutware</v-toolbar-title>

        <v-spacer />

        <v-btn
          :prepend-icon="theme.global.name.value === 'light' ? 'mdi-weather-sunny' : 'mdi-weather-night'"
          text="Toggle Theme"
          slim
          @click="toggleTheme"
        />
      </v-app-bar>

      <v-navigation-drawer
        v-model="drawer"
        permanent
        expand-on-hover
        :rail
      >
        <v-list-item
          to="/account"

          :prepend-avatar="user?.user_metadata.picture"
          :title="user?.user_metadata.name || user?.email"
          lines="one"
        />
        <v-list density="compact" nav>
          <v-divider />
          <v-list-item v-for="{ icon: prependIcon, title, to } in links" :key="to" :prepend-icon :title :to />
        </v-list>
        <template #append />
      </v-navigation-drawer>

      <v-main>
        <v-container :fluid="isWide" :style="isWide ? { maxWidth: '95%', marginInline: 'auto', paddingRight: '1.5rem' } : undefined">
          <slot />
        </v-container>
      </v-main>
    </v-app>
  </div>
</template>

<style lang="sass">
::view-transition-old(root), ::view-transition-new(root), ::view-transition-old(*), ::view-transition-new(*)
  animation-duration: 200ms
@media (prefers-reduced-motion)
  ::view-transition-old, ::view-transition-new
    animation: none
.app-copy
  position: fixed !important
  z-index: -1 !important
  pointer-events: none !important
  contain: size style !important
  overflow: clip !important

.app-transition
  --clip-size: 0
  --clip-pos: 0 0
  clip-path: circle(var(--clip-size) at var(--clip-pos))
  transition: clip-path .35s ease-out
</style>
